import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Section } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import api, { errMsg } from "@/lib/api";

const W0 = { goal: "Build muscle, improve strength", days_per_week: 4, experience: "intermediate", equipment: "full gym", session_minutes: 60, constraints: "" };
const M0 = { goal: "Fat loss while keeping muscle", calories: 2000, protein: 150, diet: "no restrictions", meals_per_day: 4, days: 3, allergies: "", cuisine: "Indian" };

function Fields({ spec, value, onChange, equipmentOptions = [] }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {spec.map(([k, label, type, wide]) => (
        <div key={k} className={wide ? "sm:col-span-2" : ""}>
          <Label>{label}</Label>
          {type === "area" ? <Textarea value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="mt-1" data-testid={`ai-${k}-input`} />
            : k === "equipment" ? <Select value={value[k]} onValueChange={(v) => onChange({ ...value, [k]: v })}>
              <SelectTrigger className="mt-1" data-testid={`ai-${k}-input`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full gym">Full gym</SelectItem>
                {(equipmentOptions || []).map((item) => <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
            : <Input type={type} value={value[k]} onChange={(e) => onChange({ ...value, [k]: type === "number" ? Number(e.target.value) : e.target.value })} className="mt-1" data-testid={`ai-${k}-input`} />}
        </div>
      ))}
    </div>
  );
}

const WSPEC = [["goal", "Goal", "text", true], ["days_per_week", "Days / week", "number"], ["session_minutes", "Session minutes", "number"],
  ["experience", "Experience", "text"], ["equipment", "Equipment", "text"], ["constraints", "Injuries / constraints", "area", true]];
const MSPEC = [["goal", "Goal", "text", true], ["calories", "Calories / day", "number"], ["protein", "Protein (g)", "number"],
  ["meals_per_day", "Meals / day", "number"], ["days", "Days to plan", "number"], ["diet", "Diet type", "text"], ["cuisine", "Cuisine", "text"], ["allergies", "Allergies", "area", true]];

export default function AIStudio() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [clients] = useLoad("/clients");
  const [equipmentOptions] = useLoad("/equipment");
  const [clientId, setClientId] = useState(params.get("client") || "none");
  const [type, setType] = useState("workout");
  const [w, setW] = useState(W0);
  const [m, setM] = useState(M0);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const body = { ...(type === "workout" ? w : m), client_id: clientId === "none" ? null : clientId };
      const { data } = await api.post(`/ai/${type}-draft`, body);
      toast.success("Draft ready - review before it reaches anyone");
      nav(`/coach/plans/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Claude-powered drafting" title="AI studio" testid="ai-studio-title" />
      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <Section>
          <Tabs value={type} onValueChange={setType}>
            <TabsList className="bg-[#111827] border border-[#1E293B] mb-6">
              <TabsTrigger value="workout" data-testid="ai-tab-workout">Workout draft</TabsTrigger>
              <TabsTrigger value="meal" data-testid="ai-tab-meal">Meal-plan draft</TabsTrigger>
            </TabsList>
            <div className="mb-4">
              <Label>For client (optional - adds their goal, health notes & latest check-in as context)</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="mt-1" data-testid="ai-client-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No client - save as library draft</SelectItem>
                  {(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <TabsContent value="workout"><Fields spec={WSPEC} value={w} onChange={setW} equipmentOptions={equipmentOptions} /></TabsContent>
            <TabsContent value="meal"><Fields spec={MSPEC} value={m} onChange={setM} /></TabsContent>
          </Tabs>
          <Button onClick={generate} disabled={busy} className="mt-6 h-11 px-6" data-testid="ai-generate-btn">
            <Sparkles size={16} className={`mr-2 ${busy ? "animate-spin" : ""}`} />{busy ? "Drafting… (20-60s)" : `Generate ${type} draft`}
          </Button>
        </Section>
        <Section title="Guardrails" action={<ShieldCheck size={18} className="text-emerald-400" />}>
          <ul className="text-sm text-slate-400 space-y-3">
            <li>Every AI output is saved as an <span className="text-amber-400">editable draft</span>. Clients see nothing until you approve.</li>
            <li>Meal plans below 1200 kcal/day are flagged automatically.</li>
            <li>Unsafe terms (steroids, diuretics, starvation, etc.) and excessive volume are flagged for your attention.</li>
            <li>No medical diagnoses or medication advice are requested from the model.</li>
            <li>Each generation and approval is recorded in the audit log.</li>
          </ul>
        </Section>
      </div>
    </div>
  );
}
